import React from "react";
import { Heart, ExternalLink, Phone, Mail, MapPin, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";

interface Company {
  name: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  street_address: string | null;
  website: string | null;
  logo_url: string | null;
  phone: string | null;
  email: string | null;
  established_year: number | null;
}

interface CompanyCardProps {
  company: Company;
}

export const CompanyCard: React.FC<CompanyCardProps> = ({ company }) => {
  const cityStateZip = [company.city, company.state, company.zip].filter(Boolean).join(", ");
  const hasAddress = !!(company.street_address || cityStateZip);
  const hasContact = !!(company.phone || company.email || hasAddress);

  const mapsQuery = [company.street_address, company.city, company.state, company.zip]
    .filter(Boolean)
    .join(" ");

  const tagline = company.established_year
    ? `Serving families since ${company.established_year}`
    : "Serving families with compassion and craftsmanship";

  const websiteHref = company.website
    ? (company.website.startsWith("http") ? company.website : `https://${company.website}`)
    : null;

  return (
    <div className="bg-gradient-to-br from-card to-muted/20 rounded-xl shadow-sm border-2 border-primary/10 overflow-hidden transition-all duration-300 hover:shadow-lg hover:border-primary/20">
      {/* Header */}
      <div className="border-b border-primary/10 bg-primary/5 px-6 py-4 flex items-center gap-2">
        <Heart className="h-5 w-5 text-primary flex-shrink-0" />
        <h3 className="font-medium text-foreground leading-snug">
          {company.name
            ? <>Headstone crafted with care by <span className="font-semibold">{company.name}</span></>
            : "Headstone crafted with care"}
        </h3>
      </div>

      <div className="p-6 space-y-5">
        {/* Logo + name + tagline */}
        <div className="flex items-start gap-4">
          {company.logo_url ? (
            <img
              src={company.logo_url}
              alt={company.name ?? "Company logo"}
              className="h-20 w-20 rounded-lg object-contain border bg-white flex-shrink-0 shadow-sm"
            />
          ) : (
            <div className="h-20 w-20 rounded-lg border bg-muted flex items-center justify-center flex-shrink-0">
              <Building2 className="h-10 w-10 text-muted-foreground" />
            </div>
          )}
          <div className="space-y-1 min-w-0 pt-1">
            {company.name && (
              <p className="text-lg font-semibold text-foreground leading-tight">{company.name}</p>
            )}
            <p className="text-sm text-muted-foreground italic">{tagline}</p>
            {cityStateZip && !hasAddress && (
              <p className="text-sm text-muted-foreground">{cityStateZip}</p>
            )}
          </div>
        </div>

        {/* Services callout */}
        <div className="bg-muted/40 rounded-lg px-4 py-3 text-sm text-muted-foreground">
          Contact us for monument care, restoration, and custom headstones
        </div>

        {/* Website CTA */}
        {websiteHref && (
          <Button asChild variant="default" className="w-full">
            <a href={websiteHref} target="_blank" rel="noopener noreferrer">
              Visit Our Website
              <ExternalLink className="h-4 w-4 ml-2" />
            </a>
          </Button>
        )}

        {/* Contact details */}
        {hasContact && (
          <div className="border-t pt-4 space-y-3">
            {company.phone && (
              <a
                href={`tel:${company.phone}`}
                className="flex items-center gap-3 text-sm text-primary underline underline-offset-2 hover:opacity-80 transition-opacity"
              >
                <Phone className="h-4 w-4 flex-shrink-0" />
                <span>{company.phone}</span>
              </a>
            )}
            {company.email && (
              <div className="flex items-center gap-3 text-sm text-muted-foreground">
                <Mail className="h-4 w-4 flex-shrink-0" />
                <span className="break-all">{company.email}</span>
              </div>
            )}
            {hasAddress && (
              <a
                href={`https://maps.google.com/?q=${encodeURIComponent(mapsQuery)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 text-sm text-primary underline underline-offset-2 hover:opacity-80 transition-opacity"
              >
                <MapPin className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <div>
                  {company.street_address && <p>{company.street_address}</p>}
                  {cityStateZip && <p>{cityStateZip}</p>}
                </div>
              </a>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
